import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaArrowLeft } from "react-icons/fa";
import { blogService, BlogPost } from "../services/firebase";
import "./BlogDetail.css";
import RightArrow from "assets/icone/RightArrow";
import RightArrowMoblie from "assets/icone/RightArrowMoblie";

// Component for responsive RightArrow
const ResponsiveRightArrow: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth >= 390 && window.innerWidth <= 809);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return isMobile ? <RightArrowMoblie /> : <RightArrow />;
};

const BlogDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();

  // Get current language
  const currentLanguage = i18n.language;

  // Get localized content based on current language
  const getLocalizedTitle = () => {
    if (!post) return "";
    if (currentLanguage === "ru" && post.titleRu) {
      return post.titleRu;
    }
    return post.title;
  };

  const getLocalizedContent = () => {
    if (!post) return "";
    if (currentLanguage === "ru" && post.contentRu) {
      return post.contentRu;
    }
    return post.content;
  };

  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await blogService.getById(id);
        setPost(data);
      } catch (error) {
                setError(t("cannotLoadPost"));
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id, t]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return t("notPublished");
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("he-IL");
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return t("notPublished");
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="page-blog-detail-container">
        <div className="loading-container">{t("loading")}</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="page-blog-detail-container">
        <div className="error-container">{error || t("postNotFound")}</div>
      </div>
    );
  }

  return (
    <>
      {/* <CurvedAccent className="curved-accent-blog" /> */}

      <div className="page-blog-detail-container">
        <Link className="back-button-blog-detail" to="/blog-detail">
          <ResponsiveRightArrow />
          {t("backToBlog")}
        </Link>

        {post.imageUrl && (
          <img
            className="blog-detail-image"
            src={post.imageUrl}
            alt={post.title}
          />
        )}

        <div className="blog-detail-header">
          <h1 className="blog-detail-title">{getLocalizedTitle()}</h1>
          <div className="blog-detail-meta">
            <div className="blog-detail-meta-item">
              {formatDate(post.publishedAt || post.createdAt)} |{" "}
              {formatTime(post.createdAt)}
            </div>
          </div>
        </div>

        <div
          className="blog-detail-content"
          dangerouslySetInnerHTML={{ __html: getLocalizedContent() }}
        />
      </div>
    </>
  );
};

export default BlogDetail;
